import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, of, timer } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PerformersService } from '@app/modules/account-modules/performers.service';
import { InputValidators } from '@app/core/validators/input.validator';

export interface PerformerSlugEditDialogData {
  performerId: string;
  currentSlug: string;
}

@UntilDestroy()
@Component({
  selector: 'app-performer-slug-edit-dialog',
  templateUrl: './slug-edit-dialog.component.html',
  styleUrls: ['./slug-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformerSlugEditDialogComponent {
  form: FormGroup;
  errorMessage: string = null;
  saving = false;

  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private fb: FormBuilder,
    private performersService: PerformersService,
    private inputValidators: InputValidators,
    private cdr: ChangeDetectorRef
  ) {
    const data = (this.config.data || {}) as PerformerSlugEditDialogData;
    const initial = (data.currentSlug || '').toLowerCase().trim();
    this.form = this.fb.group({
      slug: [
        initial,
        [Validators.required, Validators.maxLength(100), this.inputValidators.checkSlugValue.bind(this.inputValidators)],
        [this.slugUniquenessValidator()]
      ]
    });
  }

  private slugUniquenessValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const slug = (control.value || '').toString().toLowerCase().trim();
      if (!slug || slug.length < 3) return of(null);
      return timer(200).pipe(
        switchMap(() => this.performersService.checkSlug(slug)),
        map(res => (res?.isTaken ? { slugTaken: true } : null)),
        catchError(() => of(null))
      );
    };
  }

  onSlugInput(value: string): void {
    const normalized = (value || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
    this.form.get('slug').setValue(normalized, { emitEvent: true });
  }

  onSave(): void {
    this.errorMessage = null;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.cdr.markForCheck();
      return;
    }
    const data = (this.config.data || {}) as PerformerSlugEditDialogData;
    const slug = (this.form.get('slug').value || '').toString().toLowerCase().trim();
    this.saving = true;
    this.cdr.markForCheck();
    this.performersService.updatePerformerSlug(data.performerId, slug).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.saving = false;
        this.ref.close(slug);
        this.cdr.markForCheck();
      },
      err => {
        this.saving = false;
        this.errorMessage = (err && (err.message || err.error)) || 'Не удалось сохранить. Попробуйте позже.';
        this.cdr.markForCheck();
      }
    );
  }

  onCancel(): void {
    this.ref.close();
  }
}
